import { ICoordinate } from '@autopylot-internal/tiles-client';
import { useInstance } from '../../support/ioc/useInstance';
import { PreMissionWeatherState } from '../PreMissionWeatherState.ts';
import { useSubscription } from '@autopylot-internal/state';
import { useEffect } from 'react';
import { WeatherSummaryDrawerButton } from '../WeatherButton.tsx';
import { MissionCreationWeatherModal } from './MissionCreationWeatherModal.tsx';
import { RevenueCatModel } from '../../paywall/revenue-cat/RevenueCatModel.ts';
import { IonItem, IonText } from '@ionic/react';
import { ForwardIcon } from '../../components/icons/Icons.tsx';
import { ITriggerPaywallSource, PaywallDialogViewModel } from '../../paywall/PaywallDialogViewModel.tsx';

type ISO8601 = string;

interface IPreMissionWeatherInDrawerProps {
    location: ICoordinate;
    startTime: ISO8601;
    endTime: ISO8601;
    header: React.ReactElement;
    trackingFrom?: string;
    paywallReturnTo?: string;
    paywallTriggerSource?: ITriggerPaywallSource;
}

export const PreMissionWeatherInDrawer: React.FC<IPreMissionWeatherInDrawerProps> = ({ location, startTime, endTime, header, trackingFrom, paywallReturnTo, paywallTriggerSource = 'weather summary' }) => {
    const state = useInstance(PreMissionWeatherState);
    useSubscription(() => state);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const paywallVM = useInstance(PaywallDialogViewModel);

    const isUpgraded = revenueCat.hasUpgraded();

    useEffect(() => {
        if (isUpgraded) {
            state.updateProps({ location, startTime, endTime, trackingFrom });
        }
    }, [isUpgraded, location, startTime, endTime, state, trackingFrom]);

    if (!isUpgraded) {
        return (
            <IonItem button detail={false} lines="none" style={{fontSize: 15, '--padding-start': 0, '--padding-end': 0, '--inner-padding-end': 0}} onClick={() => {
                paywallVM.requirePurchase({ triggerSource: paywallTriggerSource, returnTo: paywallReturnTo });
            }}>
                <IonText color="medium">See forecasts with a free trial of</IonText>&nbsp;<IonText color="primary"><b>AutoPylot+</b></IonText>
                <ForwardIcon slot="end" size="small" color="medium" />
            </IonItem>
        );
    }

    const weatherSummary = state.getWeatherSummary();

    const buildStatus = () => {
        if (state.fetchWeatherError){
            return state.fetchWeatherError.errorCode || 'error';
        }
        if (weatherSummary){
            return 'loaded';
        }
        return 'loading';
    };
    return (
        <div data-id="weather"
            data-forecast={weatherSummary?.forecastStart}
            data-location={JSON.stringify(weatherSummary?.location)}
            data-status={buildStatus()}
        >
            <WeatherSummaryDrawerButton
                {...state.getWeatherSummaryProps()}
                onClick={state.showWeatherDetails}
                trackingFrom={trackingFrom}
            />
            <MissionCreationWeatherModal header={header} />
        </div>
    );
};
